import { useMemo, useState } from 'react';
import {
  Box,
  HStack,
  IconButton,
  SimpleGrid,
  Skeleton,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import {
  formatCurrency,
  bigSum,
  AccountTransaction,
  canonicalTransactionCategory,
  Debt,
  InventoryChange,
  InventoryChangeReason,
  PurchaseOrderPayment,
  StoreInventoryValue,
  StoreProduct,
  StoreProductChange,
  Supplier,
  SupplierValuation,
  TransactionCategory,
} from '@bofrak-backend/shared';
import { formatDate } from '@bofrak-backend/shared-i18n';
import { RepeatIcon } from '@chakra-ui/icons';
import { FailedValue, pressableProps, SectionHeader } from './MetricCard';
import { InventoryValueOverlay } from './InventoryValueOverlay';
import { MoneyValueDrawer } from './MoneyValueDrawer';
import { ClientDebtsDrawer } from './ClientDebtsDrawer';
import { SupplierDebtsDrawer } from './SupplierDebtsDrawer';
import { GrossProfitDrawer } from './GrossProfitDrawer';
import { InventoryGainLossDrawer } from './InventoryGainLossDrawer';
import { CostRevaluationDrawer } from './CostRevaluationDrawer';
import { TransactionCategoryDrawer } from './TransactionCategoryDrawer';
import { mergeReversalCandidates } from '../ResponsiveTransactionTable';
import { tShared, useSharedUiTranslation } from '../../../i18n';
import type { DashboardStatus } from '../../hooks/use-dashboard-queries';
import { combineStatus, type QueryStatus } from '../../utils/query-status';

/**
 * Literal anchors so the i18n extractor sees the row labels, which stay
 * English (they drive the click routing below) and are translated at render
 * time through `t(row.label)`.
 */
export const VALUATION_LABEL_KEYS = [
  tShared('Inventory'),
  tShared('Money'),
  tShared('Client Debts'),
  tShared('Supplier Due'),
  tShared('Est. Total'),
  tShared('Gross Profit'),
  tShared('Inventory Count/Correction'),
  tShared('Cost Changes'),
  tShared('Valuation adjustments'),
  tShared('Sales Receipts'),
  tShared('Sales Refunds'),
  tShared('Debt Payments'),
  tShared('Loan Payments'),
  tShared('Loans Given'),
  tShared('Transfers In'),
  tShared('Transfers Out'),
  tShared('Investments'),
  tShared('Profit Payouts'),
  tShared('Supplier Payments'),
  tShared('Supplier Refunds'),
  tShared('Expenses'),
  tShared('Corrections'),
  tShared('Opening Corrections'),
  tShared('Closing Corrections'),
  tShared('Debt Payment Reversals'),
  tShared('Loan Payment Reversals'),
  tShared('Loan Given Reversals'),
  tShared('Expense Reversals'),
  tShared('Profit Payout Reversals'),
  tShared('Investment Reversals'),
];

interface ValuationRow {
  label: string;
  start: number;
  end: number;
  startStatus: QueryStatus;
  endStatus: QueryStatus;
  bold?: boolean;
  isHeaderOrFooter?: boolean;
}

interface ValuationSlideProps {
  dateRange: { start: Date | null; end: Date | null };
  storeValuation: {
    inventoryStart: number;
    inventoryEnd: number;
    moneyStart: number;
    moneyEnd: number;
    clientDebtsStart: number;
    clientDebtsEnd: number;
    supplierDueStart: number;
    supplierDueEnd: number;
    totalStart: number;
    totalEnd: number;
  };
  salesMetrics: { grossProfit: number };
  transactionBreakdown: Record<TransactionCategory, number>;
  inventoryGainOrLoss: number;
  /**
   * Value impact of manual cost edits on on-hand stock in the period
   * (Σ inventory_value_change of cost-updated records). Explains the part
   * of the Est. Total difference that no transaction or inventory change
   * carries.
   */
  costRevaluation?: number;
  /** The cost-edit records behind `costRevaluation`, for the drawer. */
  costRevaluationChanges?: StoreProductChange[];
  /**
   * Price approvals at an owner cost in the period: (owner − carried
   * cost) × units. Its own row, only when non-zero (Orders & Transfers).
   */
  valuationAdjustments?: number;
  /** Per-request state: each cell waits only for its own data. */
  status: DashboardStatus;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  inventoryValues?: StoreInventoryValue[];
  allStoreDebts?: Debt[];
  supplierValuation?: SupplierValuation;
  purchaseOrderPayments?: PurchaseOrderPayment[];
  allInventoryChanges?: InventoryChange[];
  suppliersMap?: Map<string, Supplier>;
  accountTransactions?: AccountTransaction[];
  /** Partner reversals for `accountTransactions` (API `reversals` field). */
  accountTransactionsReversals?: AccountTransaction[];
  storeProducts?: StoreProduct[];
  /**
   * Debt/loan payments in the period from the debts ledger, split into money
   * payments and non-money refund settlements (payment_type DEBT). Falls back
   * to money-transaction sums when absent.
   */
  debtPaymentsPeriod?: {
    money: number;
    refundSettled: number;
    total: number;
  } | null;
}

function ValuationCell({
  value,
  bold,
  status,
  colored,
  prefixSign,
}: {
  value: number | string;
  bold?: boolean;
  status: QueryStatus;
  colored?: boolean;
  prefixSign?: boolean;
}) {
  if (status.isError) return <FailedValue fontSize="10px" justify="flex-end" />;

  const numValue = typeof value === 'number' ? value : 0;
  const color = colored
    ? numValue > 0
      ? 'green.600'
      : numValue < 0
        ? 'red.500'
        : 'gray.500'
    : bold
      ? 'gray.800'
      : 'gray.600';

  const display =
    typeof value === 'string'
      ? value
      : colored || prefixSign
        ? `${numValue >= 0 ? '+' : ''}${formatCurrency(numValue, true)}`
        : formatCurrency(numValue, true);

  return (
    <Skeleton isLoaded={!status.isLoading}>
      <Text
        fontSize="10px"
        textAlign="right"
        whiteSpace="nowrap"
        fontWeight={bold ? 'bold' : 'normal'}
        color={color}>
        {display}
      </Text>
    </Skeleton>
  );
}

export function ValuationSlide({
  dateRange,
  storeValuation,
  salesMetrics,
  transactionBreakdown,
  inventoryGainOrLoss,
  costRevaluation = 0,
  costRevaluationChanges = [],
  valuationAdjustments = 0,
  status,
  onRefresh,
  isRefreshing,
  inventoryValues = [],
  allStoreDebts = [],
  supplierValuation,
  purchaseOrderPayments = [],
  allInventoryChanges = [],
  suppliersMap,
  accountTransactions = [],
  accountTransactionsReversals = [],
  storeProducts = [],
  debtPaymentsPeriod = null,
}: ValuationSlideProps) {
  const { t } = useSharedUiTranslation();
  const inventoryOverlay = useDisclosure();
  const moneyDrawer = useDisclosure();
  const debtsDrawer = useDisclosure();
  const supplierDrawer = useDisclosure();
  const grossProfitDrawer = useDisclosure();
  const gainLossDrawer = useDisclosure();
  const revaluationDrawer = useDisclosure();
  const categoryDrawer = useDisclosure();
  const [selectedCategory, setSelectedCategory] =
    useState<TransactionCategory | null>(null);

  const categoryTransactions = useMemo(
    () =>
      selectedCategory
        ? accountTransactions.filter(
            (tx) =>
              canonicalTransactionCategory(tx.category) === selectedCategory,
          )
        : [],
    [selectedCategory, accountTransactions],
  );

  // Goods received on POs raise Supplier Due (receive-based due), so the
  // supplier drawer needs these to place its history steps correctly.
  const poReceiveChanges = useMemo(
    () =>
      allInventoryChanges.filter(
        (c) => c.reason === InventoryChangeReason.PURCHASE_ORDER,
      ),
    [allInventoryChanges],
  );
  // Each cell waits only for the request(s) it is computed from. The
  // opening client debts also need the period's debt payments.
  const componentRows: ValuationRow[] = [
    {
      label: 'Inventory',
      start: storeValuation.inventoryStart,
      end: storeValuation.inventoryEnd,
      startStatus: status.inventoryValues,
      endStatus: status.inventoryValues,
      bold: true,
    },
    {
      label: 'Money',
      start: storeValuation.moneyStart,
      end: storeValuation.moneyEnd,
      startStatus: status.snapshotsStart,
      endStatus: status.snapshotsEnd,
      bold: true,
    },
    {
      label: 'Client Debts',
      start: storeValuation.clientDebtsStart,
      end: storeValuation.clientDebtsEnd,
      startStatus: combineStatus(status.allStoreDebts, status.debtPayments),
      endStatus: status.allStoreDebts,
      bold: true,
    },
    {
      label: 'Supplier Due',
      start: storeValuation.supplierDueStart,
      end: storeValuation.supplierDueEnd,
      startStatus: status.supplierValuation,
      endStatus: status.supplierValuation,
      bold: true,
    },
  ];
  const rows: ValuationRow[] = [
    ...componentRows,
    {
      label: 'Est. Total',
      start: storeValuation.totalStart,
      end: storeValuation.totalEnd,
      startStatus: combineStatus(...componentRows.map((r) => r.startStatus)),
      endStatus: combineStatus(...componentRows.map((r) => r.endStatus)),
      bold: true,
      isHeaderOrFooter: true,
    },
  ];

  /** Human-friendly labels for each transaction category. */
  const categoryLabel: Record<TransactionCategory, string> = {
    [TransactionCategory.SALES_RECEIPT]: 'Sales Receipts',
    [TransactionCategory.SALES_REFUND]: 'Sales Refunds',
    [TransactionCategory.DEBT_PAYMENT]: 'Debt Payments',
    [TransactionCategory.LOAN_PAYMENT]: 'Loan Payments',
    [TransactionCategory.LOAN_GIVEN]: 'Loans Given',
    [TransactionCategory.TRANSFER_IN]: 'Transfers In',
    [TransactionCategory.TRANSFER_OUT]: 'Transfers Out',
    [TransactionCategory.INVESTMENT]: 'Investments',
    [TransactionCategory.DIVIDEND]: 'Profit Payouts',
    [TransactionCategory.SUPPLIER_PAYMENT]: 'Supplier Payments',
    [TransactionCategory.SUPPLIER_REFUND]: 'Supplier Refunds',
    [TransactionCategory.EXPENSE]: 'Expenses',
    [TransactionCategory.CORRECTION]: 'Corrections',
    [TransactionCategory.OPENING_CORRECTION]: 'Opening Corrections',
    [TransactionCategory.CLOSING_CORRECTION]: 'Closing Corrections',
    [TransactionCategory.DEBT_PAYMENT_REVERSAL]: 'Debt Payment Reversals',
    [TransactionCategory.LOAN_PAYMENT_REVERSAL]: 'Loan Payment Reversals',
    [TransactionCategory.LOAN_GIVEN_REVERSAL]: 'Loan Given Reversals',
    [TransactionCategory.EXPENSE_REVERSAL]: 'Expense Reversals',
    [TransactionCategory.DIVIDEND_REVERSAL]: 'Profit Payout Reversals',
    [TransactionCategory.INVESTMENT_REVERSAL]: 'Investment Reversals',
  };

  const excludedCategories = new Set<TransactionCategory>([
    TransactionCategory.SALES_RECEIPT,
    TransactionCategory.SALES_REFUND,
    TransactionCategory.DEBT_PAYMENT,
    TransactionCategory.LOAN_PAYMENT,
    TransactionCategory.LOAN_GIVEN,
    TransactionCategory.TRANSFER_IN,
    TransactionCategory.TRANSFER_OUT,
    TransactionCategory.SUPPLIER_PAYMENT,
    TransactionCategory.SUPPLIER_REFUND,
    // Reversals are folded into their counterpart originals by the breakdown,
    // so they must not render as separate rows here.
    TransactionCategory.DEBT_PAYMENT_REVERSAL,
    TransactionCategory.LOAN_PAYMENT_REVERSAL,
    TransactionCategory.LOAN_GIVEN_REVERSAL,
    TransactionCategory.EXPENSE_REVERSAL,
    TransactionCategory.DIVIDEND_REVERSAL,
    TransactionCategory.INVESTMENT_REVERSAL,
  ]);

  const categoryRows = Object.values(TransactionCategory)
    .filter((cat) => !excludedCategories.has(cat))
    .map((cat) => ({
      label: categoryLabel[cat],
      value: transactionBreakdown[cat],
      status: status.accountTransactions,
    }));

  const breakdownRows = [
    {
      label: 'Gross Profit',
      value: salesMetrics.grossProfit,
      status: status.salesReport,
    },
    ...categoryRows,
    {
      label: 'Inventory Count/Correction',
      value: inventoryGainOrLoss,
      status: status.inventoryChanges,
    },
    {
      // Manual cost edits revalue on-hand stock: the Est. Total moves with
      // no transaction or inventory change to explain it, so it gets its
      // own breakdown row.
      label: 'Cost Changes',
      value: costRevaluation,
      status: status.costUpdates,
    },
    ...(valuationAdjustments
      ? [
          {
            label: 'Valuation adjustments',
            value: valuationAdjustments,
            status: status.inventoryChanges,
          },
        ]
      : []),
  ];

  const breakdownTotal = bigSum(breakdownRows.map((r) => r.value)).toNumber();
  const breakdownTotalStatus = combineStatus(
    ...breakdownRows.map((r) => r.status),
  );

  return (
    <>
      {/* ── Store Valuation Grid ── */}
      <HStack>
        <SectionHeader color="teal.400" label={t('Store Valuation')} />
        {onRefresh && (
          <IconButton
            aria-label={t('Refresh valuation')}
            icon={<RepeatIcon />}
            size="xs"
            variant="ghost"
            colorScheme="teal"
            mb={1.5}
            isLoading={isRefreshing}
            onClick={onRefresh}
          />
        )}
      </HStack>

      <SimpleGrid
        columns={4}
        w="full"
        bg="white"
        borderRadius="md"
        border="1px solid"
        borderColor="gray.200"
        overflow="hidden">
        {/* Header row */}
        <Box bg="gray.50" px={2} py={1.5}>
          <Text fontSize="10px" fontWeight="bold" color="gray.400">
            &nbsp;
          </Text>
        </Box>
        {[dateRange.start, dateRange.end, null].map((date, i) => (
          <Box key={i} bg="gray.50" px={1} py={1.5}>
            <Text
              fontSize="10px"
              fontWeight="bold"
              color="gray.500"
              textAlign="right"
              whiteSpace="nowrap">
              {date
                ? formatDate(date, 'dd.MM.yy HH:mm')
                : i === 2
                  ? t('Difference')
                  : '—'}
            </Text>
          </Box>
        ))}

        {/* Data rows */}
        {rows.map((row) => {
          const net = row.end - row.start;
          const netStatus = combineStatus(row.startStatus, row.endStatus);
          // A row with a failed cell retries on tap instead of opening the
          // drawer, which would have no data to show. The label cell is the
          // row's one keyboard stop; the value cells stay tap targets only.
          const isClickable =
            netStatus.isError ||
            row.label === 'Inventory' ||
            row.label === 'Money' ||
            row.label === 'Client Debts' ||
            row.label === 'Supplier Due';
          const handleRowClick = netStatus.isError
            ? netStatus.onRetry
            : isClickable
              ? row.label === 'Inventory'
                ? inventoryOverlay.onOpen
                : row.label === 'Money'
                  ? moneyDrawer.onOpen
                  : row.label === 'Client Debts'
                    ? debtsDrawer.onOpen
                    : supplierDrawer.onOpen
              : undefined;
          const hoverBg =
            row.label === 'Inventory'
              ? 'teal.50'
              : row.label === 'Money'
                ? 'blue.50'
                : row.label === 'Client Debts'
                  ? 'orange.50'
                  : row.label === 'Supplier Due'
                    ? 'purple.50'
                    : undefined;
          const labelColor =
            row.label === 'Inventory'
              ? 'teal.600'
              : row.label === 'Money'
                ? 'blue.600'
                : row.label === 'Client Debts'
                  ? 'orange.600'
                  : row.label === 'Supplier Due'
                    ? 'purple.600'
                    : row.bold
                      ? 'gray.800'
                      : 'gray.700';
          return [
            <Box
              key={`${row.label}-l`}
              px={2}
              py={1.5}
              bg={row.isHeaderOrFooter ? 'gray.50' : 'white'}
              borderTop="1px solid"
              borderColor="gray.100"
              cursor={isClickable ? 'pointer' : undefined}
              _hover={isClickable ? { bg: hoverBg } : undefined}
              {...pressableProps(handleRowClick)}>
              <Text
                fontSize="2xs"
                color={labelColor}
                fontWeight={row.bold ? 'bold' : 'medium'}
                whiteSpace="nowrap">
                {t(row.label)}
              </Text>
            </Box>,
            <Box
              key={`${row.label}-s`}
              px={1}
              py={1.5}
              bg={row.isHeaderOrFooter ? 'gray.50' : 'white'}
              borderTop="1px solid"
              borderColor="gray.100"
              cursor={isClickable ? 'pointer' : undefined}
              _hover={isClickable ? { bg: hoverBg } : undefined}
              onClick={handleRowClick}>
              <ValuationCell
                value={row.start}
                bold={row.bold}
                status={row.startStatus}
              />
            </Box>,
            <Box
              key={`${row.label}-e`}
              px={1}
              py={1.5}
              bg={row.isHeaderOrFooter ? 'gray.50' : 'white'}
              borderTop="1px solid"
              borderColor="gray.100"
              cursor={isClickable ? 'pointer' : undefined}
              _hover={isClickable ? { bg: hoverBg } : undefined}
              onClick={handleRowClick}>
              <ValuationCell
                value={row.end}
                bold={row.bold}
                status={row.endStatus}
              />
            </Box>,
            <Box
              key={`${row.label}-n`}
              px={1}
              py={1.5}
              bg={row.isHeaderOrFooter ? 'gray.50' : 'white'}
              borderTop="1px solid"
              borderColor="gray.100"
              cursor={isClickable ? 'pointer' : undefined}
              _hover={isClickable ? { bg: hoverBg } : undefined}
              onClick={handleRowClick}>
              <ValuationCell
                value={net}
                bold={row.bold}
                status={netStatus}
                colored={row.isHeaderOrFooter}
                prefixSign
              />
            </Box>,
          ];
        })}
      </SimpleGrid>

      {/* The identity behind the grid, so the Supplier Due minus sign is
          visible on screen: its row falling makes the total RISE. */}
      <Text fontSize="10px" color="gray.700" mt={1} px={1}>
        {t('Est. Total = Inventory + Money + Client Debts − Supplier Due')}
      </Text>

      {/* ── Change Breakdown ── */}
      <SectionHeader color="orange.400" label={t('Change Breakdown')} />

      {breakdownRows.map((row) => {
        const isGrossProfit = row.label === 'Gross Profit';
        const isGainLoss = row.label === 'Inventory Count/Correction';
        const isRevaluation = row.label === 'Cost Changes';
        const isCategoryRow = !isGrossProfit && !isGainLoss && !isRevaluation;
        const isClickable = true;
        const handleClick = row.status.isError
          ? row.status.onRetry
          : isGrossProfit
            ? grossProfitDrawer.onOpen
            : isGainLoss
              ? gainLossDrawer.onOpen
              : isRevaluation
                ? revaluationDrawer.onOpen
                : isCategoryRow
                  ? () => {
                      const cat = Object.values(TransactionCategory).find(
                        (c) => categoryLabel[c] === row.label,
                      );
                      if (cat) {
                        setSelectedCategory(cat);
                        categoryDrawer.onOpen();
                      }
                    }
                  : undefined;
        return (
          <HStack
            key={row.label}
            w="full"
            justify="space-between"
            py={1.5}
            px={2}
            borderBottom="1px solid"
            borderColor="gray.100"
            cursor={isClickable ? 'pointer' : undefined}
            _hover={isClickable ? { bg: 'gray.50' } : undefined}
            {...pressableProps(handleClick)}>
            <Text fontWeight={'bold'} fontSize="2xs" color="gray.700">
              {t(row.label)}
            </Text>
            {row.status.isError ? (
              <FailedValue fontSize="xs" justify="flex-end" />
            ) : (
              <Skeleton isLoaded={!row.status.isLoading}>
                <Text
                  fontSize="xs"
                  fontWeight="medium"
                  whiteSpace="nowrap"
                  color={
                    row.value > 0
                      ? 'green.600'
                      : row.value < 0
                        ? 'red.500'
                        : 'gray.500'
                  }>
                  {row.value >= 0 ? '+' : ''}
                  {formatCurrency(row.value, true)}
                </Text>
              </Skeleton>
            )}
          </HStack>
        );
      })}

      {/* Breakdown Total */}
      <HStack
        w="full"
        justify="space-between"
        py={1.5}
        px={2}
        bg="gray.50"
        borderTop="2px solid"
        borderColor="gray.300"
        borderRadius="0 0 md md"
        cursor={breakdownTotalStatus.isError ? 'pointer' : undefined}
        {...pressableProps(
          breakdownTotalStatus.isError
            ? breakdownTotalStatus.onRetry
            : undefined,
        )}>
        <Text fontWeight="bold" fontSize="2xs" color="gray.800">
          {t('Total')}
        </Text>
        {breakdownTotalStatus.isError ? (
          <FailedValue fontSize="xs" justify="flex-end" />
        ) : (
          <Skeleton isLoaded={!breakdownTotalStatus.isLoading}>
            <Text
              fontSize="xs"
              fontWeight="bold"
              whiteSpace="nowrap"
              color={
                breakdownTotal > 0
                  ? 'green.600'
                  : breakdownTotal < 0
                    ? 'red.500'
                    : 'gray.500'
              }>
              {breakdownTotal >= 0 ? '+' : ''}
              {formatCurrency(breakdownTotal, true)}
            </Text>
          </Skeleton>
        )}
      </HStack>

      {/* The calculation behind the breakdown total, and the check it
          exists to satisfy. */}
      <Text fontSize="10px" color="gray.700" mt={1} px={1}>
        {t(
          'Total = Gross Profit + Investments + Profit Payouts + Expenses + Corrections + Inventory Count/Correction + Cost Changes',
        )}
      </Text>

      <InventoryValueOverlay
        isOpen={inventoryOverlay.isOpen}
        onClose={inventoryOverlay.onClose}
        inventoryValues={inventoryValues}
        dateRange={dateRange}
        inventoryChanges={allInventoryChanges}
        isLoading={status.inventoryValues.isLoading}
        storeProducts={storeProducts}
        costRevaluation={costRevaluation}
      />

      <MoneyValueDrawer
        isOpen={moneyDrawer.isOpen}
        onClose={moneyDrawer.onClose}
        dateRange={dateRange}
        accountTransactions={accountTransactions}
        moneyStart={storeValuation.moneyStart}
        moneyEnd={storeValuation.moneyEnd}
      />

      <ClientDebtsDrawer
        isOpen={debtsDrawer.isOpen}
        onClose={debtsDrawer.onClose}
        dateRange={dateRange}
        allStoreDebts={allStoreDebts}
        knownStart={storeValuation.clientDebtsStart}
        knownEnd={storeValuation.clientDebtsEnd}
        debtPaymentsInPeriod={
          debtPaymentsPeriod
            ? debtPaymentsPeriod.money
            : (transactionBreakdown[TransactionCategory.DEBT_PAYMENT] || 0) +
              (transactionBreakdown[TransactionCategory.LOAN_PAYMENT] || 0)
        }
        refundSettledInPeriod={debtPaymentsPeriod?.refundSettled ?? 0}
      />

      <SupplierDebtsDrawer
        isOpen={supplierDrawer.isOpen}
        onClose={supplierDrawer.onClose}
        dateRange={dateRange}
        supplierValuation={supplierValuation}
        purchaseOrderPayments={purchaseOrderPayments}
        suppliersMap={suppliersMap}
        poReceiveChanges={poReceiveChanges}
      />

      <GrossProfitDrawer
        isOpen={grossProfitDrawer.isOpen}
        onClose={grossProfitDrawer.onClose}
        storeProducts={storeProducts}
      />

      <InventoryGainLossDrawer
        isOpen={gainLossDrawer.isOpen}
        onClose={gainLossDrawer.onClose}
        inventoryChanges={allInventoryChanges}
        storeProducts={storeProducts}
      />

      <CostRevaluationDrawer
        isOpen={revaluationDrawer.isOpen}
        onClose={revaluationDrawer.onClose}
        changes={costRevaluationChanges}
        storeProducts={storeProducts}
      />

      <TransactionCategoryDrawer
        isOpen={categoryDrawer.isOpen}
        onClose={categoryDrawer.onClose}
        category={selectedCategory}
        transactions={categoryTransactions}
        reversals={mergeReversalCandidates(
          accountTransactionsReversals,
          accountTransactions,
        )}
      />
    </>
  );
}
